<template>
  <section
    class="dash-academics-hub"
    :class="{ 'dash-academics-hub--busy': loading }"
  >
    <div class="dash-academics-hub__head">
      <div>
        <p class="dash-academics-hub__eyebrow">{{ $t("Academics") }}</p>
        <h4 class="dash-academics-hub__title">
          {{ $t("Students, classes & requests") }}
        </h4>
      </div>
      <b-spinner v-if="loading" small variant="primary" />
    </div>

    <!-- KPI strip — unified segmented rail -->
    <div class="dash-acad-rail" role="list">
      <article
        v-for="(kpi, kIdx) in kpiItems"
        :key="kpi.key"
        class="dash-acad-rail__seg"
        :class="'dash-acad-rail__seg--' + kpi.key"
        role="listitem"
        :style="{ '--seg-delay': `${kIdx * 0.06}s` }"
      >
        <span class="dash-acad-rail__accent" aria-hidden="true" />
        <div class="dash-acad-rail__content">
          <div class="dash-acad-rail__head">
            <span class="dash-acad-rail__icon" aria-hidden="true">
              <feather-icon :icon="kpi.icon" size="15" />
            </span>
            <span class="dash-acad-rail__label">{{ $t(kpi.label) }}</span>
          </div>
          <div class="dash-acad-rail__row">
            <span class="dash-acad-rail__value">{{ kpi.value }}</span>
            <span class="dash-acad-rail__chip">{{ kpi.hint }}</span>
          </div>
        </div>
      </article>
    </div>

    <b-row class="match-height">
      <!-- Class strength with gender split -->
      <b-col xl="7" lg="12" class="mb-1">
        <b-card no-body class="dash-panel dash-strength-panel dash-strength-panel--glass h-100">
          <b-card-header class="dash-panel__header dash-strength-panel__header border-0">
            <div class="dash-panel__heading">
              <span class="dash-panel__icon dash-strength-panel__icon">
                <feather-icon icon="UsersIcon" size="18" />
              </span>
              <div>
                <b-card-title class="dash-panel__title mb-0">{{
                  $t("Students by class")
                }}</b-card-title>
                <small class="dash-panel__subtitle">{{
                  $t("Headcount with gender split")
                }}</small>
              </div>
            </div>
            <div class="dash-gender-legend">
              <span class="dash-gender-legend__item">
                <i class="is-male" />{{ $t("Male") }}
              </span>
              <span class="dash-gender-legend__item">
                <i class="is-female" />{{ $t("Female") }}
              </span>
              <span v-if="hasOtherGender" class="dash-gender-legend__item">
                <i class="is-other" />{{ $t("Other") }}
              </span>
            </div>
          </b-card-header>

          <b-card-body class="dash-strength-panel__body">
            <div v-if="!strengthRows.length" class="dash-empty">
              <feather-icon icon="InboxIcon" size="30" />
              <span>{{ $t("No records found") }}</span>
            </div>

            <template v-else>
              <!-- Overall split -->
              <div class="dash-gender-total">
                <div class="dash-gender-total__bar">
                  <span
                    class="is-male"
                    :style="{ width: maleShare + '%' }"
                  />
                  <span
                    class="is-female"
                    :style="{ width: femaleShare + '%' }"
                  />
                  <span
                    v-if="hasOtherGender"
                    class="is-other"
                    :style="{ width: otherShare + '%' }"
                  />
                </div>
                <div class="dash-gender-total__meta">
                  <span class="dash-gender-stat">
                    <i class="is-male" />
                    <strong>{{ strengthTotals.male }}</strong>
                    {{ $t("Male") }} · {{ maleShare }}%
                  </span>
                  <span class="dash-gender-stat">
                    <i class="is-female" />
                    <strong>{{ strengthTotals.female }}</strong>
                    {{ $t("Female") }} · {{ femaleShare }}%
                  </span>
                  <span v-if="hasOtherGender" class="dash-gender-stat">
                    <i class="is-other" />
                    <strong>{{ strengthTotals.other }}</strong>
                    {{ $t("Other") }} · {{ otherShare }}%
                  </span>
                </div>
              </div>

              <div class="dash-class-viz">
                <div
                  v-for="(row, idx) in strengthRows.slice(0, 12)"
                  :key="'cls-' + idx"
                  class="dash-class-viz__row"
                  :style="{ '--row-delay': `${idx * 0.045}s` }"
                >
                  <span class="dash-class-viz__rank">{{ idx + 1 }}</span>
                  <div class="dash-class-viz__main">
                    <div class="dash-class-viz__head">
                      <span class="dash-class-viz__name">{{
                        row.className
                      }}</span>
                      <span class="dash-class-viz__total-pill">{{
                        row.total
                      }}</span>
                    </div>
                    <div class="dash-class-viz__counts">
                      <span class="is-m">{{ row.male }} M</span>
                      <span class="sep">·</span>
                      <span class="is-f">{{ row.female }} F</span>
                    </div>
                    <div class="dash-class-viz__bar">
                      <div class="dash-class-viz__track">
                        <div
                          class="dash-class-viz__fill"
                          :style="{ width: barWidth(row.total) + '%' }"
                        >
                          <span
                            class="is-male"
                            :style="{
                              width: share(row.male, row.total) + '%',
                            }"
                          />
                          <span
                            class="is-female"
                            :style="{
                              width: share(row.female, row.total) + '%',
                            }"
                          />
                          <span
                            v-if="row.other"
                            class="is-other"
                            :style="{
                              width: share(row.other, row.total) + '%',
                            }"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </b-card-body>
        </b-card>
      </b-col>

      <b-col xl="5" lg="12" class="mb-1">
        <div class="dash-academics-side h-100">
          <b-card no-body class="dash-panel dash-req-panel dash-req-panel--glass dash-req-panel--leave">
            <b-card-header class="dash-req-panel__header">
              <div class="dash-req-panel__title-wrap">
                <span class="dash-req-panel__glyph dash-req-panel__glyph--leave">
                  <feather-icon icon="ClockIcon" size="17" />
                </span>
                <div>
                  <b-card-title class="dash-panel__title mb-0">{{
                    $t("Leave requests")
                  }}</b-card-title>
                  <small class="dash-panel__subtitle">{{
                    $t("Pending only")
                  }}</small>
                </div>
              </div>
              <span v-if="leavePendingTotal" class="dash-req-panel__pill">{{
                leavePendingTotal
              }}</span>
            </b-card-header>

            <b-card-body class="dash-req-panel__body dash-req-panel__body--leave">
              <div v-if="leaveRows.length" class="dash-req-glass-list">
                <div
                  v-for="(row, i) in leaveRows.slice(0, 8)"
                  :key="'leave-' + i"
                  class="dash-req-glass-item dash-req-glass-item--leave"
                  :style="{ '--row-delay': `${i * 0.05}s` }"
                >
                  <span class="dash-req-glass-item__avatar">{{
                    initials(row.name)
                  }}</span>
                  <div class="dash-req-glass-item__copy">
                    <span class="dash-req-glass-item__name">{{ row.name }}</span>
                    <span class="dash-req-glass-item__sub">{{
                      $t("Pending approval")
                    }}</span>
                  </div>
                  <span class="dash-req-glass-item__qty">{{ row.count }}</span>
                </div>
              </div>
              <div v-else class="dash-req-glass-empty">
                <feather-icon icon="CheckCircleIcon" size="20" />
                <span>{{ $t("No pending leave") }}</span>
              </div>
            </b-card-body>
          </b-card>

          <b-card no-body class="dash-panel dash-req-panel dash-req-panel--glass dash-req-panel--student">
            <b-card-header class="dash-req-panel__header">
              <div class="dash-req-panel__title-wrap">
                <span
                  class="dash-req-panel__glyph dash-req-panel__glyph--student"
                >
                  <feather-icon icon="FileTextIcon" size="17" />
                </span>
                <div>
                  <b-card-title class="dash-panel__title mb-0">{{
                    $t("Student requests")
                  }}</b-card-title>
                  <small class="dash-panel__subtitle">{{
                    $t("Pending only")
                  }}</small>
                </div>
              </div>
              <span
                v-if="studentPendingTotal"
                class="dash-req-panel__pill dash-req-panel__pill--student"
                >{{ studentPendingTotal }}</span
              >
            </b-card-header>

            <b-card-body class="dash-req-panel__body dash-req-panel__body--student">
              <div v-if="studentReqRows.length" class="dash-req-glass-list">
                <div
                  v-for="(row, i) in studentReqRows.slice(0, 8)"
                  :key="'streq-' + i"
                  class="dash-req-glass-item dash-req-glass-item--student"
                  :style="{ '--row-delay': `${i * 0.05}s` }"
                >
                  <span class="dash-req-glass-item__avatar">{{
                    initials(row.name)
                  }}</span>
                  <div class="dash-req-glass-item__copy">
                    <span class="dash-req-glass-item__name">{{ row.name }}</span>
                    <span class="dash-req-glass-item__sub">{{
                      $t("Pending approval")
                    }}</span>
                  </div>
                  <span class="dash-req-glass-item__qty">{{ row.count }}</span>
                </div>
              </div>
              <div v-else class="dash-req-glass-empty">
                <feather-icon icon="CheckCircleIcon" size="20" />
                <span>{{ $t("No pending requests") }}</span>
              </div>
            </b-card-body>
          </b-card>
        </div>
      </b-col>
    </b-row>
  </section>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BCardBody,
  BRow,
  BCol,
  BSpinner,
} from "bootstrap-vue";

export default {
  name: "DashboardAcademicsHub",
  components: {
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BRow,
    BCol,
    BSpinner,
  },
  props: {
    loading: { type: Boolean, default: false },
    strengthRows: { type: Array, default: () => [] },
    strengthTotals: {
      type: Object,
      default: () => ({ male: 0, female: 0, other: 0, total: 0 }),
    },
    leaveRows: { type: Array, default: () => [] },
    studentReqRows: { type: Array, default: () => [] },
    subData: { type: Array, default: () => [] },
  },
  computed: {
    maxClassTotal() {
      const rows = this.strengthRows || [];
      if (!rows.length) return 1;
      return Math.max(...rows.map((r) => Number(r.total) || 0), 1);
    },
    /** Percentages are normalised so the three segments always total 100. */
    genderPercents() {
      const t = this.strengthTotals || {};
      const total =
        (Number(t.male) || 0) +
        (Number(t.female) || 0) +
        (Number(t.other) || 0);
      if (total <= 0) return { male: 0, female: 0, other: 0 };
      return {
        male: Math.round(((Number(t.male) || 0) / total) * 1000) / 10,
        female: Math.round(((Number(t.female) || 0) / total) * 1000) / 10,
        other: Math.round(((Number(t.other) || 0) / total) * 1000) / 10,
      };
    },
    maleShare() {
      return this.genderPercents.male;
    },
    femaleShare() {
      return this.genderPercents.female;
    },
    otherShare() {
      return this.genderPercents.other;
    },
    hasOtherGender() {
      return Number(this.strengthTotals.other) > 0;
    },
    leavePendingTotal() {
      return (this.leaveRows || []).reduce(
        (sum, row) => sum + (Number(row.count) || 0),
        0
      );
    },
    studentPendingTotal() {
      return (this.studentReqRows || []).reduce(
        (sum, row) => sum + (Number(row.count) || 0),
        0
      );
    },
    kpiItems() {
      return [
        {
          key: "total",
          label: "Total students",
          icon: "UsersIcon",
          value: this.strengthTotals.total,
          hint: `${this.strengthRows.length} ${this.$t("classes")}`,
        },
        {
          key: "m",
          label: "Male",
          icon: "UserIcon",
          value: this.strengthTotals.male,
          hint: `${this.maleShare}%`,
        },
        {
          key: "f",
          label: "Female",
          icon: "UserCheckIcon",
          value: this.strengthTotals.female,
          hint: `${this.femaleShare}%`,
        },
        {
          key: "leave",
          label: "Leave pending",
          icon: "ClockIcon",
          value: this.leavePendingTotal,
          hint: `${this.leaveRows.length} ${this.$t("staff")}`,
        },
        {
          key: "req",
          label: "Student requests",
          icon: "FileTextIcon",
          value: this.studentPendingTotal,
          hint: this.$t("Leaving requests"),
        },
      ];
    },
  },
  methods: {
    share(part, total) {
      const t = Number(total) || 0;
      if (!t) return 0;
      return Math.round((Number(part) / t) * 1000) / 10;
    },
    barWidth(total) {
      const t = Number(total) || 0;
      return Math.max(12, Math.round((t / this.maxClassTotal) * 100));
    },
    initials(name) {
      const parts = String(name || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean);
      if (!parts.length) return "?";
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    },
  },
};
</script>

<style scoped lang="scss">
.dash-academics-hub {
  margin-top: 0.65rem;
  margin-bottom: 0.35rem;
  padding: 1rem 1.1rem 1.05rem;
  border-radius: 22px;
  border: 1px solid rgba(115, 103, 240, 0.1);
  background: rgba(255, 255, 255, 0.52);
  backdrop-filter: blur(16px) saturate(1.12);
  -webkit-backdrop-filter: blur(16px) saturate(1.12);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.9) inset,
    0 14px 40px rgba(115, 103, 240, 0.07);
  animation: dashAcadHubIn 0.65s cubic-bezier(0.22, 1, 0.36, 1) both;

  &--busy {
    opacity: 0.92;
  }
}

@keyframes dashAcadHubIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dash-academics-hub__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.85rem;
}

.dash-academics-hub__eyebrow {
  margin: 0;
  font-size: 0.6875rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #7367f0;
}

.dash-academics-hub__title {
  margin: 0.2rem 0 0;
  font-size: 1.05rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #2f2b3d;
}

/* ------------------------------------------------------------------ KPI rail */

.dash-acad-rail {
  display: flex;
  flex-wrap: nowrap;
  margin-bottom: 0.95rem;
  border-radius: 22px;
  background: linear-gradient(
    145deg,
    rgba(255, 255, 255, 0.82) 0%,
    rgba(243, 240, 255, 0.55) 48%,
    rgba(255, 255, 255, 0.7) 100%
  );
  backdrop-filter: blur(16px) saturate(1.15);
  -webkit-backdrop-filter: blur(16px) saturate(1.15);
  border: 1px solid rgba(115, 103, 240, 0.14);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.95) inset,
    0 12px 32px rgba(115, 103, 240, 0.08);
  overflow: hidden;
}

@media (max-width: 1199px) {
  .dash-acad-rail {
    flex-wrap: wrap;
  }
}

.dash-acad-rail__seg {
  --seg-accent: #7367f0;
  --seg-soft: rgba(115, 103, 240, 0.12);
  --seg-glow: rgba(115, 103, 240, 0.08);

  position: relative;
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: stretch;
  padding: 0.85rem 0.8rem 0.82rem;
  animation: dashAcadSegIn 0.55s cubic-bezier(0.22, 1, 0.36, 1) var(--seg-delay, 0s)
    both;
  transition: background 0.25s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.45);
  }

  &:not(:first-child)::after {
    content: "";
    position: absolute;
    left: 0;
    top: 14%;
    bottom: 14%;
    width: 1px;
    background: linear-gradient(
      180deg,
      transparent,
      rgba(115, 103, 240, 0.22),
      transparent
    );
    pointer-events: none;
  }

  &--total {
    --seg-accent: #7367f0;
    --seg-soft: rgba(115, 103, 240, 0.14);
    --seg-glow: rgba(115, 103, 240, 0.1);
  }
  &--m {
    --seg-accent: #2563eb;
    --seg-soft: rgba(37, 99, 235, 0.12);
    --seg-glow: rgba(59, 130, 246, 0.1);
  }
  &--f {
    --seg-accent: #db2777;
    --seg-soft: rgba(219, 39, 119, 0.12);
    --seg-glow: rgba(244, 114, 182, 0.1);
  }
  &--leave {
    --seg-accent: #ea580c;
    --seg-soft: rgba(234, 88, 12, 0.12);
    --seg-glow: rgba(251, 146, 60, 0.1);
  }
  &--req {
    --seg-accent: #6366f1;
    --seg-soft: rgba(99, 102, 241, 0.12);
    --seg-glow: rgba(129, 140, 248, 0.1);
  }
}

@media (max-width: 1199px) {
  .dash-acad-rail__seg {
    flex: 1 1 calc(33.333% - 1px);
    min-width: 9.5rem;

    &:nth-child(n + 2)::after {
      display: none;
    }

    &:nth-child(4)::before,
    &:nth-child(2)::before {
      content: "";
      position: absolute;
      left: 8%;
      right: 8%;
      top: 0;
      height: 1px;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(115, 103, 240, 0.15),
        transparent
      );
    }
  }
}

@media (max-width: 575px) {
  .dash-acad-rail {
    flex-direction: column;
  }

  .dash-acad-rail__seg {
    flex: 1 1 auto;
    min-width: 100%;

    &:not(:first-child)::after {
      left: 8%;
      right: 8%;
      top: 0;
      bottom: auto;
      width: auto;
      height: 1px;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(115, 103, 240, 0.15),
        transparent
      );
    }
  }
}

.dash-acad-rail__accent {
  width: 3px;
  flex-shrink: 0;
  margin-right: 0.65rem;
  border-radius: 999px;
  background: linear-gradient(180deg, var(--seg-accent), rgba(115, 103, 240, 0.35));
  box-shadow: 0 0 12px var(--seg-glow);
}

.dash-acad-rail__content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.4rem;
}

.dash-acad-rail__head {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;
}

.dash-acad-rail__icon {
  width: 1.625rem;
  height: 1.625rem;
  flex-shrink: 0;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--seg-accent);
  background: var(--seg-soft);
  border: 1px solid rgba(115, 103, 240, 0.12);
}

.dash-acad-rail__seg--m .dash-acad-rail__icon {
  border-color: rgba(37, 99, 235, 0.15);
}
.dash-acad-rail__seg--f .dash-acad-rail__icon {
  border-color: rgba(219, 39, 119, 0.15);
}
.dash-acad-rail__seg--leave .dash-acad-rail__icon {
  border-color: rgba(234, 88, 12, 0.15);
}
.dash-acad-rail__seg--req .dash-acad-rail__icon {
  border-color: rgba(99, 102, 241, 0.15);
}

.dash-acad-rail__label {
  font-size: 0.625rem;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dash-acad-rail__row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.45rem;
  min-height: 1.75rem;
}

.dash-acad-rail__value {
  font-size: clamp(1.15rem, 1.35vw, 1.45rem);
  font-weight: 800;
  letter-spacing: -0.035em;
  line-height: 1;
  color: #1a1625;
  font-variant-numeric: tabular-nums;
}

.dash-acad-rail__chip {
  flex-shrink: 0;
  max-width: 48%;
  padding: 0.18rem 0.45rem;
  border-radius: 999px;
  font-size: 0.5625rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: var(--seg-accent);
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(115, 103, 240, 0.14);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dash-acad-rail__seg--m .dash-acad-rail__chip {
  border-color: rgba(37, 99, 235, 0.18);
}
.dash-acad-rail__seg--f .dash-acad-rail__chip {
  border-color: rgba(219, 39, 119, 0.18);
}
.dash-acad-rail__seg--leave .dash-acad-rail__chip {
  border-color: rgba(234, 88, 12, 0.18);
}
.dash-acad-rail__seg--req .dash-acad-rail__chip {
  border-color: rgba(99, 102, 241, 0.18);
}

@keyframes dashAcadSegIn {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* -------------------------------------------------------------- strength */

.dash-strength-panel__icon {
  background: linear-gradient(135deg, rgba(219, 234, 254, 0.95) 0%, rgba(252, 231, 243, 0.95) 100%);
  color: #7367f0;
  border: 1px solid rgba(115, 103, 240, 0.12);
}

.dash-strength-panel__header {
  flex-wrap: wrap;
  gap: 0.5rem;
  padding-bottom: 0.65rem !important;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.5) 0%,
    rgba(255, 255, 255, 0) 100%
  ) !important;
}

.dash-gender-legend {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex-shrink: 0;

  &__item {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    font-size: 0.65rem;
    font-weight: 700;
    color: #64748b;
    white-space: nowrap;

    i {
      width: 7px;
      height: 7px;
      border-radius: 999px;
      display: inline-block;
    }
  }
}

i.is-male {
  background: linear-gradient(135deg, #60a5fa, #2563eb);
}
i.is-female {
  background: linear-gradient(135deg, #f472b6, #db2777);
}
i.is-other {
  background: linear-gradient(135deg, #cbd5e1, #94a3b8);
}

.dash-strength-panel__body {
  max-height: min(520px, 58vh);
  overflow: auto;
  overscroll-behavior: contain;
  padding: 0 0.85rem 0.85rem !important;
  scrollbar-width: thin;
  scrollbar-color: rgba(115, 103, 240, 0.35) transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 999px;
    background: linear-gradient(180deg, #b9b3f8, #7367f0);
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }
}

.dash-gender-total {
  padding: 0.75rem 0.85rem;
  margin-bottom: 0.75rem;
  border-radius: 18px;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.72) 0%,
    rgba(237, 233, 254, 0.45) 100%
  );
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.88);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.95) inset,
    0 8px 24px rgba(115, 103, 240, 0.08);
  animation: dashAcadBlockIn 0.55s cubic-bezier(0.22, 1, 0.36, 1) 0.15s both;

  &__bar {
    display: flex;
    height: 14px;
    border-radius: 999px;
    overflow: hidden;
    background: rgba(235, 233, 241, 0.9);
    margin-bottom: 0.45rem;
    box-shadow: inset 0 1px 3px rgba(15, 23, 42, 0.06);

    span {
      display: block;
      flex: 0 0 auto;
      height: 100%;
      min-width: 0;
      transition: width 0.85s cubic-bezier(0.22, 1, 0.36, 1);
      box-shadow: inset 0 -1px 0 rgba(255, 255, 255, 0.25);
    }

    span.is-male {
      background: linear-gradient(90deg, #60a5fa, #2563eb);
    }
    span.is-female {
      background: linear-gradient(90deg, #f472b6, #db2777);
    }
    span.is-other {
      background: linear-gradient(90deg, #cbd5e1, #94a3b8);
    }
  }

  &__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.45rem 0.9rem;
  }
}

.dash-gender-stat {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.72rem;
  font-weight: 600;
  color: #64748b;

  i {
    width: 8px;
    height: 8px;
    border-radius: 3px;
    display: inline-block;
    flex-shrink: 0;
  }

  strong {
    color: #0f172a;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
  }

  &--sm {
    font-size: 0.7rem;

    i {
      width: 6px;
      height: 6px;
      border-radius: 999px;
    }
  }
}

.dash-strength-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.dash-class-viz {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  &__row {
    display: flex;
    align-items: flex-start;
    gap: 0.55rem;
    padding: 0.55rem 0.65rem;
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.62);
    border: 1px solid rgba(255, 255, 255, 0.9);
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.95) inset,
      0 4px 14px rgba(115, 103, 240, 0.05);
    animation: dashAcadRowIn 0.5s cubic-bezier(0.22, 1, 0.36, 1) var(--row-delay, 0s)
      both;
    transition:
      background 0.2s ease,
      transform 0.2s ease,
      box-shadow 0.2s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.82);
      transform: translateX(3px);
      box-shadow:
        0 1px 0 rgba(255, 255, 255, 0.95) inset,
        0 8px 22px rgba(115, 103, 240, 0.1);
    }
  }

  &__rank {
    width: 1.5rem;
    height: 1.5rem;
    margin-top: 0.05rem;
    font-size: 0.62rem;
    font-weight: 800;
    color: #7367f0;
    text-align: center;
    flex-shrink: 0;
    font-variant-numeric: tabular-nums;
    border-radius: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(115, 103, 240, 0.1);
  }

  &__main {
    flex: 1;
    min-width: 0;
  }

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    margin-bottom: 0.2rem;
  }

  &__name {
    font-weight: 800;
    font-size: 0.82rem;
    color: #1e293b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__total-pill {
    flex-shrink: 0;
    min-width: 1.65rem;
    padding: 0.12rem 0.45rem;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: 800;
    color: #2f2b3d;
    font-variant-numeric: tabular-nums;
    background: linear-gradient(135deg, rgba(237, 233, 254, 0.95), rgba(255, 255, 255, 0.9));
    border: 1px solid rgba(115, 103, 240, 0.15);
    box-shadow: 0 2px 8px rgba(115, 103, 240, 0.1);
  }

  &__counts {
    display: inline-flex;
    align-items: baseline;
    gap: 0.35rem;
    margin-bottom: 0.35rem;
    font-size: 0.68rem;
    font-weight: 700;
    font-variant-numeric: tabular-nums;

    .is-m {
      color: #2563eb;
    }
    .is-f {
      color: #db2777;
    }
    .sep {
      color: #cbd5e1;
      font-weight: 600;
    }
  }

  &__bar {
    width: 100%;
    height: 12px;
    display: flex;
    align-items: center;
  }

  &__track {
    flex: 1;
    width: 100%;
    height: 100%;
    border-radius: 999px;
    background: rgba(226, 232, 240, 0.65);
    box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.06);
    overflow: hidden;
  }

  &__fill {
    display: flex;
    height: 100%;
    min-width: 6%;
    max-width: 100%;
    border-radius: 999px;
    overflow: hidden;
    transition: width 0.85s cubic-bezier(0.22, 1, 0.36, 1);

    span {
      display: block;
      flex: 0 0 auto;
      height: 100%;
      min-width: 0;
      transition: width 0.85s cubic-bezier(0.22, 1, 0.36, 1);
    }

    .is-male {
      background: linear-gradient(90deg, #60a5fa, #2563eb);
    }
    .is-female {
      background: linear-gradient(90deg, #f472b6, #db2777);
    }
    .is-other {
      background: linear-gradient(90deg, #cbd5e1, #94a3b8);
    }
  }

  &__bar-bg {
    display: none;
  }
}

.dash-strength-row {
  padding: 0.5rem 0.65rem;
  border-radius: 13px;
  border: 1px solid #f1f5f9;
  background: #fff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: #e2e8f0;
    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.05);
  }

  &__head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 0.3rem;
  }

  &__name {
    font-weight: 800;
    font-size: 0.82rem;
    color: #1e293b;
  }

  &__total {
    font-weight: 800;
    font-size: 0.88rem;
    color: #0f172a;
    font-variant-numeric: tabular-nums;
  }

  &__bar {
    display: flex;
    height: 9px;
    border-radius: 999px;
    overflow: hidden;
    background: #f1f5f9;

    span {
      display: block;
      height: 100%;
      transition: width 0.8s cubic-bezier(0.22, 1, 0.36, 1);
    }
  }

  &__meta {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    margin-top: 0.3rem;
  }

  &__pct {
    margin-left: auto;
    font-size: 0.66rem;
    font-weight: 800;
    color: #94a3b8;
    font-variant-numeric: tabular-nums;
  }
}

/* ---------------------------------------------------------------- panels */

.dash-academics-side {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.dash-strength-panel--glass,
.dash-req-panel--glass {
  border-radius: 20px !important;
  background: rgba(255, 255, 255, 0.58) !important;
  backdrop-filter: blur(14px) saturate(1.1);
  -webkit-backdrop-filter: blur(14px) saturate(1.1);
  border: 1px solid rgba(255, 255, 255, 0.82) !important;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.92) inset,
    0 10px 28px rgba(115, 103, 240, 0.06) !important;
  animation: dashAcadPanelIn 0.6s cubic-bezier(0.22, 1, 0.36, 1) 0.2s both;
}

@keyframes dashAcadPanelIn {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes dashAcadBlockIn {
  from {
    opacity: 0;
    transform: scale(0.99);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes dashAcadRowIn {
  from {
    opacity: 0;
    transform: translateX(-8px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.dash-strength-panel {
  overflow: hidden;
}

.dash-req-panel {
  overflow: hidden;
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.72) !important;
  background: rgba(255, 255, 255, 0.42) !important;

  &::before {
    display: none;
  }

  &--student {
    animation-delay: 0.28s;
  }
}

.dash-req-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.85rem 1rem 0.5rem !important;
  border-bottom: 1px solid rgba(115, 103, 240, 0.08) !important;
  background: rgba(255, 255, 255, 0.35) !important;
}

.dash-req-panel__title-wrap {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  min-width: 0;
}

.dash-req-panel__glyph {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.65);
  backdrop-filter: blur(8px);
  box-shadow: none;
  border: 1px solid rgba(255, 255, 255, 0.9);

  &--leave {
    color: #ea580c;
  }
  &--student {
    color: #7367f0;
  }
}

.dash-req-panel__pill {
  min-width: 1.65rem;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 800;
  text-align: center;
  background: rgba(255, 255, 255, 0.7);
  color: #64748b;
  border: 1px solid rgba(115, 103, 240, 0.12);
  box-shadow: none;

  &--student {
    color: #64748b;
    border-color: rgba(115, 103, 240, 0.12);
  }
}

.dash-req-panel__body {
  padding: 0.65rem 0.75rem 0.8rem !important;
  background: rgba(255, 255, 255, 0.28);

  &--leave,
  &--student {
    min-height: 6.5rem;
  }
}

.dash-req-glass-list {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: 0.35rem;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.65);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.85);
}

.dash-req-glass-item {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 0.55rem;
  padding: 0.5rem 0.55rem;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.8);
  animation: dashAcadRowIn 0.45s cubic-bezier(0.22, 1, 0.36, 1) var(--row-delay, 0s)
    both;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.78);
  }

  &__avatar {
    width: 2rem;
    height: 2rem;
    border-radius: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.625rem;
    font-weight: 800;
    letter-spacing: 0.02em;
    color: #475569;
    background: rgba(241, 245, 249, 0.95);
    border: 1px solid rgba(226, 232, 240, 0.95);
  }

  &--leave &__avatar {
    color: #c2410c;
    background: rgba(255, 247, 237, 0.85);
    border-color: rgba(251, 146, 60, 0.2);
  }

  &--student &__avatar {
    color: #5b21b6;
    background: rgba(237, 233, 254, 0.85);
    border-color: rgba(115, 103, 240, 0.18);
  }

  &__copy {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
  }

  &__name {
    font-size: 0.8125rem;
    font-weight: 700;
    color: #1e293b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__sub {
    font-size: 0.625rem;
    font-weight: 600;
    color: #94a3b8;
  }

  &__qty {
    min-width: 1.5rem;
    padding: 0.1rem 0.4rem;
    border-radius: 8px;
    font-size: 0.75rem;
    font-weight: 800;
    text-align: center;
    font-variant-numeric: tabular-nums;
    color: #334155;
    background: rgba(255, 255, 255, 0.85);
    border: 1px solid rgba(226, 232, 240, 0.9);
  }
}

.dash-req-glass-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  min-height: 5.5rem;
  padding: 1rem;
  border-radius: 14px;
  text-align: center;
  font-size: 0.78rem;
  font-weight: 600;
  color: #94a3b8;
  background: rgba(255, 255, 255, 0.32);
  border: 1px dashed rgba(115, 103, 240, 0.14);

  svg {
    color: #7367f0;
    opacity: 0.75;
  }
}

.dash-req-stack {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.dash-req-tile {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 0.55rem;
  padding: 0.55rem 0.65rem;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.92);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.95) inset,
    0 4px 14px rgba(15, 23, 42, 0.04);
  animation: dashAcadRowIn 0.45s cubic-bezier(0.22, 1, 0.36, 1) var(--row-delay, 0s)
    both;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.95) inset,
      0 10px 22px rgba(115, 103, 240, 0.1);
  }

  &--leave {
    border-left: 3px solid rgba(251, 146, 60, 0.65);
  }

  &--student {
    border-left: 3px solid rgba(115, 103, 240, 0.55);
  }

  &__avatar {
    width: 2.15rem;
    height: 2.15rem;
    border-radius: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.65rem;
    font-weight: 800;
    flex-shrink: 0;
    color: #fff;
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.12);
  }

  &--leave &__avatar {
    background: linear-gradient(145deg, #fb923c, #ea580c);
  }

  &--student &__avatar {
    background: linear-gradient(145deg, #9e95f5, #7367f0);
  }

  &__text {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.08rem;
  }

  &__name {
    font-size: 0.82rem;
    font-weight: 800;
    color: #1e293b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__meta {
    font-size: 0.6rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #94a3b8;
  }

  &__count {
    min-width: 1.75rem;
    padding: 0.15rem 0.5rem;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: 800;
    text-align: center;
    font-variant-numeric: tabular-nums;
    background: rgba(241, 245, 249, 0.95);
    color: #334155;
    border: 1px solid rgba(226, 232, 240, 0.9);
  }
}

.dash-empty--glass {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  padding: 1.25rem 0.75rem;
  border-radius: 16px;
  text-align: center;
  font-size: 0.78rem;
  font-weight: 600;
  color: #94a3b8;
  background: rgba(255, 255, 255, 0.45);
  border: 1px dashed rgba(115, 103, 240, 0.18);

  .dash-empty__icon {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #7367f0;
    background: rgba(115, 103, 240, 0.1);
  }
}

.dash-req-minimal {
  list-style: none;
  margin: 0;
  padding: 0;
}

.dash-req-minimal__row {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 0.45rem;
  padding: 0.45rem 0.4rem;
  border-radius: 12px;
  transition: background 0.2s ease, transform 0.2s ease;
  animation: dashAcadRowIn 0.45s cubic-bezier(0.22, 1, 0.36, 1) var(--row-delay, 0s)
    both;

  &:hover {
    background: rgba(115, 103, 240, 0.05);
  }

  + .dash-req-minimal__row {
    border-top: 1px solid rgba(235, 233, 241, 0.9);
  }
}

.dash-req-minimal__avatar {
  width: 2rem;
  height: 2rem;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  font-weight: 800;
  flex-shrink: 0;

  &--leave {
    color: #c2410c;
    background: linear-gradient(145deg, #ffedd5, #fed7aa);
  }
  &--student {
    color: #4338ca;
    background: linear-gradient(145deg, #e0e7ff, #c7d2fe);
  }
}

.dash-req-minimal__name {
  font-size: 0.82rem;
  font-weight: 800;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dash-req-minimal__count {
  min-width: 1.65rem;
  padding: 0.12rem 0.45rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 800;
  text-align: center;
  background: #f1f5f9;
  color: #334155;
  font-variant-numeric: tabular-nums;
}

/* legacy request cards — unused */
.dash-req-panel__icon--leave {
  background: #fff7ed;
  color: #ea580c;
}

.dash-req-panel__icon--student {
  background: #eef2ff;
  color: #4f46e5;
}

.dash-req-panel__badge {
  padding: 0.18rem 0.5rem;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 800;
  flex-shrink: 0;

  &--leave {
    background: #fff7ed;
    color: #c2410c;
  }
  &--req {
    background: #eef2ff;
    color: #4338ca;
  }
}

.dash-req-list {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.dash-req-card {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.45rem 0.5rem;
  border-radius: 12px;
  border: 1px solid #f1f5f9;
  background: #fff;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: #fbfcfe;
    border-color: #e8ecf3;
  }

  &__avatar {
    width: 2rem;
    height: 2rem;
    border-radius: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 0.68rem;
    font-weight: 800;
    color: #c2410c;
    background: linear-gradient(135deg, #ffedd5, #fed7aa);

    &--req {
      color: #4338ca;
      background: linear-gradient(135deg, #e0e7ff, #c7d2fe);
    }
  }

  &__body {
    min-width: 0;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.06rem;
  }

  &__name {
    font-size: 0.8rem;
    font-weight: 800;
    color: #1e293b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__sub {
    font-size: 0.68rem;
    font-weight: 600;
    color: #94a3b8;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__dates {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.66rem;
    font-weight: 700;
    color: #64748b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    svg {
      flex-shrink: 0;
      color: #94a3b8;
    }
  }

  &__side {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.2rem;
    flex-shrink: 0;
  }

  &__days {
    font-size: 0.66rem;
    font-weight: 800;
    color: #475569;
    padding: 0.1rem 0.35rem;
    border-radius: 6px;
    background: #f1f5f9;
    font-variant-numeric: tabular-nums;
  }

  &__status {
    font-size: 0.6rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.03em;

    &.is-pending {
      color: #b45309;
    }
    &.is-approved {
      color: #047857;
    }
    &.is-rejected {
      color: #b91c1c;
    }
  }
}

.dash-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  padding: 1.5rem 0;
  color: #cbd5e1;
  font-size: 0.76rem;
  font-weight: 700;

  &--sm {
    padding: 1rem 0;
  }
}

/* --------------------------------------------------------------- subject */

.dash-subject-panel__icon {
  background: #ecfdf5;
  color: #059669;
}

.dash-subject-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 1fr auto;
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 0.45rem;

  &__name {
    font-size: 0.78rem;
    font-weight: 700;
    color: #334155;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__track {
    height: 6px;
    border-radius: 999px;
    background: #f1f5f9;
    overflow: hidden;
  }

  &__fill {
    display: block;
    height: 100%;
    border-radius: 999px;
    background: linear-gradient(90deg, #6366f1, #8b5cf6);
  }

  &__n {
    font-weight: 800;
    font-size: 0.78rem;
    color: #0f172a;
    min-width: 2rem;
    text-align: right;
  }
}

@media (prefers-reduced-motion: reduce) {
  .dash-academics-hub,
  .dash-academics-kpi,
  .dash-strength-panel--glass,
  .dash-req-panel--glass,
  .dash-gender-total,
  .dash-class-viz__row,
  .dash-req-minimal__row {
    animation: none !important;
  }

  .dash-gender-total__bar span,
  .dash-class-viz__fill,
  .dash-class-viz__fill span,
  .dash-strength-row__bar span {
    transition: none !important;
  }
}
</style>
